// src/api/messages-api.js
import { gql } from '@apollo/client';
import client from './apollo-client';

// Ticket Queries
export const GET_TICKETS = gql`
  query GetTickets($filters: TicketFiltersInput) {
    tickets(filters: $filters) {
      id
      subject
      status
      priority
      category
      userId
      assignedTo
      firstMessageId
      lastMessageAt
      resolvedAt
      messageCount
      hasUnreadMessages
      user {
        id
        firstName
        lastName
        email
        userType
        pharmacy {
          id
          namePharmacy
          telPharmacy
          region {
            id
            nomRegion
          }
          ville {
            id
            nomVilleVillage
          }
        }
        ownedLaboratory {
          id
          nom
        }
      }
      assignedAdmin {
        id
        firstName
        lastName
        email
      }
      firstMessage {
        id
        content
        date
      }
      messages {
        id
        content
        adminReply
        date
        user {
          id
          firstName
          lastName
          email
        }
        createdAt
        updatedAt
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_TICKET = gql`
  query GetTicket($id: ID!) {
    ticket(id: $id) {
      id
      subject
      status
      priority
      category
      userId
      assignedTo
      firstMessageId
      lastMessageAt
      resolvedAt
      messageCount
      hasUnreadMessages
      user {
        id
        firstName
        lastName
        email
        userType
        tel
        pharmacy {
          id
          namePharmacy
          telPharmacy
          region {
            id
            nomRegion
          }
          ville {
            id
            nomVilleVillage
          }
        }
        ownedLaboratory {
          id
          nom
        }
      }
      assignedAdmin {
        id
        firstName
        lastName
        email
      }
      firstMessage {
        id
        content
        date
      }
      messages {
        id
        content
        adminReply
        date
        user {
          id
          firstName
          lastName
          email
        }
        createdAt
        updatedAt
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_TICKETS_BY_USER = gql`
  query GetTicketsByUser($userId: ID!) {
    ticketsByUser(userId: $userId) {
      id
      subject
      status
      userId
      lastMessageAt
      resolvedAt
      messageCount
      hasUnreadMessages
      firstMessage {
        id
        content
        date
      }
      messages {
        id
        content
        adminReply
        date
        userId
        user {
          id
          firstName
          lastName
        }
        createdAt
        updatedAt
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_TICKET_UNREAD_COUNT = gql`
  query GetTicketUnreadCount {
    ticketStats
  }
`;

export const GET_TICKETS_BY_ASSIGNED_ADMIN = gql`
  query GetTicketsByAssignedAdmin($adminId: ID!) {
    ticketsByAssignedAdmin(adminId: $adminId) {
      id
      subject
      status
      priority
      category
      userId
      lastMessageAt
      resolvedAt
      messageCount
      hasUnreadMessages
      user {
        id
        firstName
        lastName
        email
        userType
        pharmacy {
          id
          namePharmacy
        }
        ownedLaboratory {
          id
          nom
        }
      }
      firstMessage {
        id
        content
        date
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_TICKET_STATS = gql`
  query GetTicketStats {
    ticketStats
  }
`;

// Ticket Mutations
export const CREATE_TICKET = gql`
  mutation CreateTicket($createTicketInput: CreateTicketInput!) {
    createTicket(createTicketInput: $createTicketInput) {
      id
      subject
      status
      priority
      category
      userId
      lastMessageAt
      messageCount
      user {
        id
        firstName
        lastName
        email
        userType
      }
      firstMessage {
        id
        content
        date
      }
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_TICKET = gql`
  mutation UpdateTicket($updateTicketInput: UpdateTicketInput!) {
    updateTicket(updateTicketInput: $updateTicketInput) {
      id
      subject
      status
      priority
      category
      assignedTo
      lastMessageAt
      resolvedAt
      assignedAdmin {
        id
        firstName
        lastName
        email
      }
      updatedAt
    }
  }
`;

export const ADD_MESSAGE_TO_TICKET = gql`
  mutation AddMessageToTicket($addMessageInput: AddMessageToTicketInput!) {
    addMessageToTicket(addMessageInput: $addMessageInput) {
      id
      content
      adminReply
      date
      ticketId
      user {
        id
        firstName
        lastName
        email
      }
      createdAt
      updatedAt
    }
  }
`;

export const REMOVE_TICKET = gql`
  mutation RemoveTicket($id: ID!) {
    removeTicket(id: $id)
  }
`;

export const MARK_TICKET_READ = gql`
  mutation MarkTicketRead($id: ID!) {
    markTicketRead(id: $id)
  }
`;

// Messages API functions with ticket support
export const messagesApi = {
  // Ticket operations
  getTickets: async (filters = null) => {
    try {
      const { data } = await client.query({
        query: GET_TICKETS,
        variables: { filters },
        fetchPolicy: 'network-only'
      });
      return data.tickets;
    } catch (error) {
      console.error('Get tickets error:', error);
      throw error;
    }
  },

  getTicket: async (id) => {
    try {
      const { data } = await client.query({
        query: GET_TICKET,
        variables: { id },
        fetchPolicy: 'network-only'
      });
      return data.ticket;
    } catch (error) {
      console.error(`Get ticket ${id} error:`, error);
      throw error;
    }
  },

  getTicketsByUser: async (userId) => {
    try {
      const { data } = await client.query({
        query: GET_TICKETS_BY_USER,
        variables: { userId },
        fetchPolicy: 'network-only'
      });
      return data.ticketsByUser;
    } catch (error) {
      console.error(`Get tickets by user ${userId} error:`, error);
      throw error;
    }
  },

  getTicketsByAssignedAdmin: async (adminId) => {
    try {
      const { data } = await client.query({
        query: GET_TICKETS_BY_ASSIGNED_ADMIN,
        variables: { adminId },
        fetchPolicy: 'network-only'
      });
      return data.ticketsByAssignedAdmin;
    } catch (error) {
      console.error(`Get tickets by admin ${adminId} error:`, error);
      throw error;
    }
  },

  getTicketStats: async () => {
    try {
      const { data } = await client.query({
        query: GET_TICKET_STATS,
        fetchPolicy: 'network-only'
      });
      return JSON.parse(data.ticketStats);
    } catch (error) {
      console.error('Get ticket stats error:', error);
      throw error;
    }
  },

  createTicket: async (ticketData) => {
    try {
      const { data } = await client.mutate({
        mutation: CREATE_TICKET,
        variables: { createTicketInput: ticketData }
      });
      return data.createTicket;
    } catch (error) {
      console.error('Create ticket error:', error);
      throw error;
    }
  },

  updateTicket: async (ticketData) => {
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_TICKET,
        variables: { updateTicketInput: ticketData }
      });
      return data.updateTicket;
    } catch (error) {
      console.error(`Update ticket ${ticketData.id} error:`, error);
      throw error;
    }
  },

  addMessageToTicket: async (messageData) => {
    try {
      const { data } = await client.mutate({
        mutation: ADD_MESSAGE_TO_TICKET,
        variables: { addMessageInput: messageData }
      });
      return data.addMessageToTicket;
    } catch (error) {
      console.error('Add message to ticket error:', error);
      throw error;
    }
  },

  removeTicket: async (id) => {
    try {
      const { data } = await client.mutate({
        mutation: REMOVE_TICKET,
        variables: { id }
      });
      return data.removeTicket;
    } catch (error) {
      console.error(`Remove ticket ${id} error:`, error);
      throw error;
    }
  },

  // Utility functions
  filterTickets: (tickets, filters) => {
    if (!filters) return tickets;

    return tickets.filter(ticket => {
      // Search term filter
      if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        const searchableText = [
          ticket.subject,
          ticket.user.firstName,
          ticket.user.lastName,
          ticket.user.email,
          ticket.firstMessage?.content || ''
        ].join(' ').toLowerCase();
        
        if (!searchableText.includes(term)) {
          return false;
        }
      }

      // Status filter
      if (filters.status && filters.status !== 'all' && ticket.status !== filters.status) {
        return false;
      }

      // Priority filter
      if (filters.priority && filters.priority !== 'all' && ticket.priority !== filters.priority) {
        return false;
      }

      // Category filter
      if (filters.category && filters.category !== 'all' && ticket.category !== filters.category) {
        return false;
      }

      // User type filter
      if (filters.userType && filters.userType !== 'all' && ticket.user.userType !== filters.userType) {
        return false;
      }

      // Assigned filter
      if (filters.assigned === 'assigned' && !ticket.assignedTo) {
        return false;
      }
      if (filters.assigned === 'unassigned' && ticket.assignedTo) {
        return false;
      }

      // Urgent filter
      if (filters.urgent && ticket.priority !== 'URGENT') {
        return false;
      }

      // Unread filter
      if (filters.unread && !ticket.hasUnreadMessages) {
        return false;
      }

      return true;
    });
  },

  sortTickets: (tickets, sortBy = 'lastMessageAt', sortOrder = 'desc') => {
    return [...tickets].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // Handle special sorting cases
      if (sortBy === 'priority') {
        const priorityOrder = { 'URGENT': 4, 'HIGH': 3, 'NORMAL': 2, 'LOW': 1 };
        aVal = priorityOrder[aVal] || 0;
        bVal = priorityOrder[bVal] || 0;
      }

      if (sortBy === 'status') {
        const statusOrder = { 'OPEN': 4, 'IN_PROGRESS': 3, 'RESOLVED': 2, 'CLOSED': 1 };
        aVal = statusOrder[aVal] || 0;
        bVal = statusOrder[bVal] || 0;
      }

      // Handle dates
      if (aVal instanceof Date || typeof aVal === 'string') {
        aVal = new Date(aVal).getTime();
      }
      if (bVal instanceof Date || typeof bVal === 'string') {
        bVal = new Date(bVal).getTime();
      }

      if (sortOrder === 'desc') {
        return bVal - aVal;
      } else {
        return aVal - bVal;
      }
    });
  }
};